import React, { useState } from 'react';
import { API_BASE } from '../../constants/urls';

const ExportButtons = ({ type, dateRange, data }) => {
  const [loading, setLoading] = useState(false);

  // Build query string for date range
  const buildQueryString = () => {
    const params = new URLSearchParams();
    if (dateRange.start_date) {
      params.append('start_date', dateRange.start_date);
    }
    if (dateRange.end_date) {
      params.append('end_date', dateRange.end_date);
    }
    return params.toString();
  };

  // Handle Excel export
  const handleExcelExport = async () => {
    try {
      setLoading(true);
      
      const queryString = buildQueryString();
      const endpoint = type === 'te' 
        ? `${API_BASE}/api/reports/export/te-excel`
        : `${API_BASE}/api/reports/export/ce-excel`;
      
      const url = queryString ? `${endpoint}?${queryString}` : endpoint;
      
      // Create download link
      const response = await fetch(url, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      // Get filename from headers or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${type}_performance_export.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error('Export error:', error);
      alert(`Failed to export ${type.toUpperCase()} data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle CSV export (client-side)
  const handleCSVExport = () => {
    if (!data || data.length === 0) {
      alert(`No ${type.toUpperCase()} data available to export`);
      return;
    }

    try {
      let csvContent = '';
      
      if (type === 'te') {
        // TE CSV headers
        csvContent = 'TE Name,TE Email,Assigned,Completed,WIP,Completion Rate (%),Total Word Count,Avg Turnover (Days),Productivity (Words/Day),Daily Capacity\n';
        
        // TE CSV data
        data.forEach(te => {
          csvContent += `"${te.te_name}","${te.te_email}",${te.assigned},${te.completed},${te.wip},${te.completion_rate},${te.total_word_count},${te.avg_turnaround_days},${te.productivity_words_per_day},${te.daily_capacity}\n`;
        });
      } else {
        // CE CSV headers
        csvContent = 'CE Name,CE Email,CE Level,Assigned,Completed,WIP,Completion Rate (%),Total Word Count,Avg Turnover (Days),Productivity (Words/Day),Daily Capacity Pages,Is Backup\n';
        
        // CE CSV data
        data.forEach(ce => {
          csvContent += `"${ce.ce_name}","${ce.ce_email}","${ce.ce_level}",${ce.assigned},${ce.completed},${ce.wip},${ce.completion_rate},${ce.total_word_count},${ce.avg_turnaround_days},${ce.productivity_words_per_day},${ce.daily_capacity_pages},${ce.is_backup}\n`;
        });
      }

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `${type}_performance_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('CSV export error:', error);
      alert(`Failed to export CSV: ${error.message}`);
    }
  };

  return (
    <div className="export-buttons">
      <div className="export-info">
        <span className="export-count">
          {data ? `${data.length} records` : 'No data'}
        </span>
      </div>
      
      <div className="export-actions">
        <button 
          className="export-button csv-button"
          onClick={handleCSVExport}
          disabled={!data || data.length === 0}
          title="Export as CSV file"
        >
          <span className="button-icon">CSV</span>
          <span className="button-text">Export CSV</span>
        </button>
        
        <button 
          className="export-button excel-button"
          onClick={handleExcelExport}
          disabled={loading}
          title="Export as Excel file (server-generated)"
        >
          {loading ? (
            <>
              <span className="loading-spinner"></span>
              <span className="button-text">Generating...</span>
            </>
          ) : (
            <>
              <span className="button-icon">XLS</span>
              <span className="button-text">Export Excel</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ExportButtons;
